import { motion } from "framer-motion";
import { MapPin, Dumbbell, Shirt, Footprints, ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const ICONS = {
    equipment: Dumbbell,
    sportswear: Shirt,
    shoes: Footprints,
};

const CATEGORY_IMAGES = {
    equipment:
        "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=compress&cs=tinysrgb&w=800&q=80",
    sportswear:
        "https://images.pexels.com/photos/2294361/pexels-photo-2294361.jpeg?auto=compress&cs=tinysrgb&w=800",
    shoes:
        "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=compress&cs=tinysrgb&w=800&q=80",
};

export const Store = () => {
    const { t, lang } = useLang();
    const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;

    return (
        <section
            id="store"
            data-testid="store-section"
            className="relative py-3 sm:py-6 bg-[var(--bg)] overflow-hidden"
        >
            <div className="absolute top-1/2 -translate-y-1/2 -right-32 w-[500px] h-[500px] bg-[var(--primary)]/10 blur-[140px] rounded-full pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14"
                >
                    <div className="max-w-2xl">
                        <span className="font-display text-[var(--primary)] text-sm tracking-[0.3em]">
                            {t.store.eyebrow}
                        </span>
                        <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                            {t.store.h2Top}
                            <br />
                            <span className="text-gradient-orange">{t.store.h2Bottom}</span>
                        </h2>
                        <p className="mt-5 text-base sm:text-lg text-neutral-400 leading-relaxed">
                            {t.store.desc}
                        </p>
                    </div>

                    <div
                        data-testid="store-region-badge"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--primary)]/10 border border-[var(--primary)]/30 text-[var(--primary)] text-xs sm:text-sm font-bold self-start"
                    >
                        <MapPin className="w-4 h-4" />
                        {t.store.regionBadge}
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
                    {t.store.categories.map((cat, i) => {
                        const Icon = ICONS[cat.key];
                        return (
                            <motion.div
                                key={cat.key}
                                data-testid={`store-${cat.key}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                whileHover={{ y: -6 }}
                                className="card-dark rounded-2xl overflow-hidden group relative min-h-[420px]"
                            >
                                <div className="absolute inset-0">
                                    <img
                                        src={CATEGORY_IMAGES[cat.key]}
                                        alt={cat.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
                                </div>

                                <div className="relative h-full p-7 sm:p-8 flex flex-col justify-end">
                                    {Icon && (
                                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--primary)]/15 border border-[var(--primary)]/30 mb-4 group-hover:bg-[var(--primary)] transition-colors">
                                            <Icon className="w-5 h-5 text-[var(--primary)] group-hover:text-black transition-colors" />
                                        </div>
                                    )}
                                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                        {cat.title}
                                    </h3>
                                    <p className="text-sm sm:text-base text-neutral-300 leading-relaxed mb-5">
                                        {cat.desc}
                                    </p>
                                    <div className="flex items-center gap-2 text-[var(--primary)] text-sm font-bold opacity-80 group-hover:opacity-100 transition-opacity">
                                        <span>{t.store.cta}</span>
                                        <Arrow className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
